import { Autocomplete, Checkbox, Chip, FormControlLabel, MenuItem, Stack, TextField } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { getEngagementTenants } from 'API/engagement';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { SEGMENTS, TENANT_STATES, type Audience } from 'types/engagement.types';

const KINDS: Audience['kind'][] = ['all', 'tenants', 'users', 'state', 'segment', 'trial'];

const toggle = <T,>(list: T[] | undefined, value: T): T[] => {
  const cur = list ?? [];
  return cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value];
};

const numOrUndef = (v: string): number | undefined => (v.trim() === '' || Number.isNaN(Number(v)) ? undefined : Number(v));

/** Audience per contract: kind + the filter that kind needs, plus the optional consent requirement. */
const AudienceEditor: React.FC<{ value: Audience; onChange(a: Audience): void; kinds?: Audience['kind'][] }> = ({
  value,
  onChange,
  kinds = KINDS,
}) => {
  const { t } = useTranslation();
  const tenants = useQuery({
    queryKey: ['engagement', 'tenants', '', '', ''],
    queryFn: () => getEngagementTenants({}),
    enabled: value.kind === 'tenants',
  });

  const setKind = (kind: Audience['kind']) => {
    const next: Audience = { kind, requireConsent: value.requireConsent };
    if (kind === 'state') next.states = ['no_pilot'];
    if (kind === 'segment') next.segments = ['inactive'];
    if (kind === 'trial') next.trialDayFrom = 20;
    onChange(next);
  };

  return (
    <Stack gap={1.5}>
      <TextField select size={'small'} label={t('engAudienceKind')} value={value.kind} onChange={(e) => setKind(e.target.value as Audience['kind'])}>
        {kinds.map((k) => (
          <MenuItem key={k} value={k}>
            {t(`engAudienceKind_${k}`)}
          </MenuItem>
        ))}
      </TextField>

      {value.kind === 'state' ? (
        <Stack direction={'row'} gap={1} flexWrap={'wrap'}>
          {TENANT_STATES.map((s) => (
            <Chip
              key={s}
              label={t(`engState_${s}`)}
              color={value.states?.includes(s) ? 'primary' : 'default'}
              variant={value.states?.includes(s) ? 'filled' : 'outlined'}
              onClick={() => onChange({ ...value, states: toggle(value.states, s) })}
            />
          ))}
        </Stack>
      ) : null}

      {value.kind === 'segment' ? (
        <Stack direction={'row'} gap={1} flexWrap={'wrap'}>
          {SEGMENTS.map((s) => (
            <Chip
              key={s}
              label={t(`engSegment_${s}`)}
              color={value.segments?.includes(s) ? 'primary' : 'default'}
              variant={value.segments?.includes(s) ? 'filled' : 'outlined'}
              onClick={() => onChange({ ...value, segments: toggle(value.segments, s) })}
            />
          ))}
        </Stack>
      ) : null}

      {value.kind === 'tenants' ? (
        <Autocomplete
          multiple
          size={'small'}
          loading={tenants.isLoading}
          options={tenants.data ?? []}
          getOptionLabel={(o) => o.name}
          isOptionEqualToValue={(a, b) => a.tenantId === b.tenantId}
          value={(tenants.data ?? []).filter((row) => value.tenantIds?.includes(row.tenantId))}
          onChange={(_, rows) => onChange({ ...value, tenantIds: rows.map((r) => r.tenantId) })}
          renderInput={(params) => <TextField {...params} label={t('engAudienceTenants')} />}
        />
      ) : null}

      {value.kind === 'users' ? (
        <TextField
          size={'small'}
          label={t('engAudienceUserIds')}
          helperText={t('engAudienceUserIdsHelp')}
          value={(value.userIds ?? []).join(', ')}
          onChange={(e) =>
            onChange({
              ...value,
              userIds: e.target.value
                .split(/[\s,]+/)
                .map((s) => s.trim())
                .filter(Boolean),
            })
          }
        />
      ) : null}

      {value.kind === 'trial' ? (
        <Stack direction={'row'} gap={1.5}>
          <TextField
            size={'small'}
            type={'number'}
            label={t('engAudienceTrialFrom')}
            value={value.trialDayFrom ?? ''}
            onChange={(e) => onChange({ ...value, trialDayFrom: numOrUndef(e.target.value) })}
          />
          <TextField
            size={'small'}
            type={'number'}
            label={t('engAudienceTrialTo')}
            value={value.trialDayTo ?? ''}
            onChange={(e) => onChange({ ...value, trialDayTo: numOrUndef(e.target.value) })}
          />
          <TextField
            size={'small'}
            type={'number'}
            label={t('engAudienceMinReplies')}
            value={value.minReplies ?? ''}
            onChange={(e) => onChange({ ...value, minReplies: numOrUndef(e.target.value) })}
          />
        </Stack>
      ) : null}

      <FormControlLabel
        control={
          <Checkbox
            size={'small'}
            checked={value.requireConsent === 'marketing-email'}
            onChange={(e) => onChange({ ...value, requireConsent: e.target.checked ? 'marketing-email' : undefined })}
          />
        }
        label={t('engAudienceRequireConsent')}
        slotProps={{ typography: { fontSize: 13 } }}
      />
    </Stack>
  );
};

export default AudienceEditor;
